import React, { useState, useEffect } from 'react';
import {
  Show,
  TextField,
  EmailField,
  DateField,
  BooleanField,
  useRecordContext,
  useRedirect,
  useDataProvider,
  useNotify,
  useRefresh,
  useUpdate,
  useDelete,
} from 'react-admin';
import ResourceBreadcrumbsWithActions from '../../components/ResourceBreadcrumbsWithActions';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Avatar,
  Stack,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Divider,
  TextField as MuiTextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  TrendingUp as OpportunityIcon,
  PersonAdd as CustomerIcon,
  PersonAdd as LeadIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ContactLogList } from '../../components/ContactLogList';
import { NavigationBreadcrumbs, BreadcrumbItem } from '../../components/NavigationBreadcrumbs';
import { LocationDialog } from '../../components/LocationDialog';
import { ConvertLeadDialog } from '../../components/ConvertLeadDialog';
import { LeadContactInfoDialog } from '../../components/LeadContactInfoDialog';
import { LeadIntelligenceDialog } from '../../components/LeadIntelligenceDialog';
import { LeadLocationMap } from '../../components/LeadLocationMap';
import { CreateOpportunityDialog } from '../../components/CreateOpportunityDialog';
import { getCustomerTypeColor } from '../../utils/customerTypeColors';

// Actions are now integrated into ResourceBreadcrumbsWithActions

const StatusChip = ({ record }: { record: any }) => {
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return { bgcolor: '#E3F2FD', color: '#1976D2' }; // Light blue - fresh, needs attention
      case 'contacted':
        return { bgcolor: '#FFF3E0', color: '#F57C00' }; // Orange - in progress, warming up
      case 'qualified':
        return { bgcolor: '#E8F5E8', color: '#388E3C' }; // Green - positive, moving forward
      case 'converted':
        return { bgcolor: '#E1F5FE', color: '#00ACC1' }; // Teal - success, completed
      case 'lost':
        return { bgcolor: '#FFEBEE', color: '#D32F2F' }; // Red - negative outcome
      case 'unqualified':
        return { bgcolor: '#F3E5F5', color: '#7B1FA2' }; // Purple - determined not suitable
      case 'nurturing':
        return { bgcolor: '#FFF8E1', color: '#FBC02D' }; // Yellow - requires patience/time
      default:
        return { bgcolor: '#F5F5F5', color: '#666666' }; // Grey - unknown/default
    }
  };

  const colorStyle = getStatusColor(record.status);

  return (
    <Chip
      label={record.status?.toUpperCase() || 'UNKNOWN'}
      size="small"
      sx={{
        bgcolor: colorStyle.bgcolor,
        color: colorStyle.color,
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-label': {
          px: 1
        }
      }}
    />
  );
};

const LeadHeader = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [convertDialog, setConvertDialog] = useState<'customer' | 'opportunity' | null>(null);
  const [converting, setConverting] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOne, { isLoading: isDeleting }] = useDelete('leads', { id: record?.id });

  if (!record) {
    return null;
  }

  const customerTypeColor = record?.customerType?.color || getCustomerTypeColor(record?.customerType?.name);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const displayName = record.displayName || 
    `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.company || 'Unknown Lead';

  const handleConversion = async (type: 'customer' | 'opportunity') => {
    try {
      setConverting(true);

      if (type === 'customer') {
        // Use the simple conversion endpoint
        const result = await dataProvider.create(`crm/leads/${record.id}/convert-to-customer`, {
          data: {},
        });
        notify('Lead successfully converted to customer!', { type: 'success' });
        redirect(`/customers/${result.data.customerId}/show`);
      } else {
        const result = await dataProvider.create('leads/convert-to-opportunity', {
          data: { leadId: record.id },
        });
        notify('Lead successfully converted to opportunity!', { type: 'success' });
        redirect(`/opportunities/${result.data.opportunityId}`);
      }

      refresh();
    } catch (error) {
      notify('Conversion failed. Please try again.', { type: 'error' });
      // Conversion error occurred, user has been notified
    } finally {
      setConverting(false);
      setConvertDialog(null);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOne();
      notify('Lead deleted successfully', { type: 'success' });
      redirect('/leads');
    } catch (error) {
      notify('Error deleting lead', { type: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
                          <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: customerTypeColor,
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              {record.company ? <BusinessIcon sx={{ fontSize: 40, color: 'white' }} /> : getInitials(displayName)}
            </Avatar>
            </Grid>

            <Grid item xs>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 'bold', color: customerTypeColor }}
              >
                {displayName}
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <StatusChip record={record} />
                {record.customerType && (
                  <Chip
                    label={record.customerType.name}
                    size="small"
                    sx={{ 
                      bgcolor: customerTypeColor,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                )}
                {record.company && (
                  <Chip
                    icon={<BusinessIcon />}
                    label={record.company}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                )}
                {record.source && (
                  <Chip label={record.source} variant="outlined" color="secondary" size="small" />
                )}
              </Stack>

              <Grid container spacing={2}>
                {record.phone && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {record.phone}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {record.location && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {[
                          record.location.streetLine1,
                          record.location.streetLine2,
                          record.location.city,
                          record.location.province,
                          record.location.postalCode
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item>
              <Stack spacing={1}>
                <MuiButton
                  variant="contained"
                  color="primary"
                  startIcon={<CustomerIcon />}
                  onClick={() => setShowConvertDialog(true)}
                  size="small"
                  disabled={record.status === 'converted'}
                >
                  Convert to Customer
                </MuiButton>
                <MuiButton
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  size="small"
                >
                  Delete Lead
                </MuiButton>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Conversion Dialogs */}
      <ConvertLeadDialog
        open={showConvertDialog}
        onClose={() => setShowConvertDialog(false)}
        leadRecord={record}
      />

      <Dialog open={convertDialog === 'opportunity'} onClose={() => setConvertDialog(null)}>
        <DialogTitle>Convert Lead to Opportunity</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to convert this lead to an opportunity? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialog(null)}>Cancel</Button>
          <Button
            onClick={() => handleConversion('opportunity')}
            variant="contained"
            disabled={converting}
          >
            {converting ? 'Converting...' : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Lead
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{displayName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ContactInformationCard = ({ contactDialogOpen, setContactDialogOpen }: { contactDialogOpen: boolean, setContactDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  if (!record) {
    return null;
  }

  const handleLocationCreated = async (location: any) => {
    try {
      await dataProvider.update('leads', {
        id: record.id,
        data: { locationId: location.id },
        previousData: record,
      });
      
      notify('Location added to lead successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Error updating lead with location', { type: 'error' });
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Contact Information
          </Typography>
          <Tooltip title="Edit contact information">
            <IconButton onClick={() => setContactDialogOpen(true)} size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={3}>
          {/* Display Name Section */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              Display Name
            </Typography>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {record.displayName || '-'}
              </Typography>
            </Box>
          </Box>

          {/* Name Section - Two Columns */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              Personal Name
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    First Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record.firstName || '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    Last Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record.lastName || '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                    Title
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record.title || '-'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Contact Methods - Two Columns */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              Contact Methods
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                    <EmailIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    {record.email ? (
                      <a href={`mailto:${record.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {record.email}
                      </a>
                    ) : '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                    <PhoneIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                    Phone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {record.phone || '-'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>


          {/* Location Section with Integrated Map */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              Location
            </Typography>
            
            {record.location ? (
              <Box sx={{ 
                p: 0, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
                overflow: 'hidden'
              }}>
                {/* Address Information */}
                <Box sx={{ p: 2, position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Tooltip title="Edit location">
                      <IconButton onClick={() => setLocationDialogOpen(true)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                    {record.location.name || 'Unnamed Location'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    {record.location.streetLine1}
                    {record.location.streetLine2 && `, ${record.location.streetLine2}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[record.location.city, record.location.province, record.location.postalCode].filter(Boolean).join(', ')}
                    {record.location.country && record.location.country !== 'Canada' && `, ${record.location.country}`}
                  </Typography>
                </Box>
                
                {/* Integrated Map */}
                <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200' }}>
                  <LeadLocationMap 
                    location={record.location} 
                    leadName={record.displayName || `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.company || 'Lead'}
                    compact={true}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'grey.300',
                textAlign: 'center',
                position: 'relative'
              }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Tooltip title="Add location">
                    <IconButton onClick={() => setLocationDialogOpen(true)} size="small">
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <LocationIcon sx={{ fontSize: 24, mb: 1, opacity: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  No location assigned
                </Typography>
              </Box>
            )}
          </Box>

          <LocationDialog
            open={locationDialogOpen}
            onClose={() => setLocationDialogOpen(false)}
            onLocationCreated={handleLocationCreated}
            initialValues={record.location || {}}
            title={record.location ? 'Edit Location' : 'Add Location'}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const LeadDetailsCard = ({ intelligenceDialogOpen, setIntelligenceDialogOpen }: { intelligenceDialogOpen: boolean, setIntelligenceDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return '#1976D2'; // Blue - fresh, needs attention
      case 'contacted':
        return '#F57C00'; // Orange - in progress, warming up
      case 'qualified':
        return '#388E3C'; // Green - positive, moving forward
      case 'proposal':
        return '#7B1FA2'; // Purple - advanced stage
      case 'converted':
        return '#00ACC1'; // Teal - success, completed
      case 'lost':
        return '#D32F2F'; // Red - negative outcome
      default:
        return '#666666'; // Grey - unknown/default
    }
  };

  if (!record) {
    return null;
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">
              Lead Intelligence
            </Typography>
            <Tooltip title="Edit lead intelligence">
              <IconButton onClick={() => setIntelligenceDialogOpen(true)} size="small">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={3}>
            {/* Lead Status, Source & Type - Three Columns */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Lead Status, Source & Type
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      Status
                    </Typography>
                    {record.status ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: getStatusColor(record.status),
                          }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {record.status}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        -
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      Lead Source
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                      {record.source || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      Customer Type
                    </Typography>
                    {record.customerType ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: record.customerType.color,
                          }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {record.customerType.name}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        -
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Company Information */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, fontWeight: 600 }}>
                <BusinessIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                Company
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {record.company || '-'}
              </Typography>
            </Box>



            {/* Assignment & Conversion */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Assignment & Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      Assigned To
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {record.assignedUser ? `${record.assignedUser.name || record.assignedUser.email}` : 'Unassigned'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      Conversion Status
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {record.convertedCustomer ? (
                        <Box component="span" sx={{ color: 'success.main' }}>
                          Converted to Customer
                        </Box>
                      ) : 'Not converted'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Financial & Timeline - Two Columns */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Financial & Timeline
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      Estimated Value
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                      {record.estimatedValue ? `$${record.estimatedValue.toLocaleString()}` : '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      Created
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(record.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

          </Stack>
        </CardContent>
      </Card>
    </>
  );
};

const AdditionalInformationCard = ({ additionalInfoDialogOpen, setAdditionalInfoDialogOpen }: { additionalInfoDialogOpen: boolean, setAdditionalInfoDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Additional Information
          </Typography>
          <Tooltip title="Edit additional information">
            <IconButton onClick={() => setAdditionalInfoDialogOpen(true)} size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {/* Notes */}
          {record.notes ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Notes
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.notes}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.secondary'
            }}>
              <Typography variant="body2">
                No additional information available
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const LeadBreadcrumbs = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  const displayName = record.displayName ||
                     `${record.firstName || ''} ${record.lastName || ''}`.trim() || 
                     record.company || 
                     `Lead #${record.id}`;

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: 'Leads',
      path: '/leads',
      icon: <LeadIcon fontSize="small" />,
    },
    {
      label: displayName,
      icon: <LeadIcon fontSize="small" />,
    },
  ];

  return <NavigationBreadcrumbs items={breadcrumbItems} />;
};

const ContactLogTab = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return <ContactLogList leadId={String(record.id)} />;
};

// Opportunities Section for the main overview tab
const OpportunitiesSection = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpportunityOpen, setCreateOpportunityOpen] = useState(false);

  const fetchOpportunities = async () => {
    if (!record?.id) return;
    
    try {
      setLoading(true);
      const response = await dataProvider.getList('opportunities', {
        pagination: { page: 1, perPage: 10 },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: { leadId: record.id },
      });
      
      // Filter opportunities to only show those that belong to this lead
      const filteredOpportunities = response.data.filter(opp => opp.leadId === record.id);
      
      setOpportunities(filteredOpportunities);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [record?.id, dataProvider]);

  if (!record) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="primary" gutterBottom>
            Opportunities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading opportunities...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Opportunities ({opportunities.length})
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {opportunities.length > 0 && (
              <Chip 
                label={`$${opportunities.reduce((sum, opp) => sum + (opp.value || 0), 0).toLocaleString()}`}
                color="primary"
                size="small"
                icon={<MoneyIcon />}
              />
            )}
            <Tooltip title="Create opportunity">
              <IconButton 
                size="small" 
                color="primary"
                onClick={() => setCreateOpportunityOpen(true)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {opportunities.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            color: 'text.secondary',
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'grey.300'
          }}>
            <OpportunityIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              No opportunities yet
            </Typography>
            <Typography variant="body2">
              Opportunities will appear here when they're created for this lead
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {opportunities.map((opportunity) => (
              <Grid item xs={12} sm={6} key={opportunity.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => redirect(`/opportunities/${opportunity.id}/show`)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        {opportunity.name}
                      </Typography>
                      <Chip 
                        label={opportunity.stage?.isClosedWon ? 'Won' : opportunity.stage?.isClosedLost ? 'Lost' : 'Open'}
                        size="small"
                        color={opportunity.stage?.isClosedWon ? 'success' : opportunity.stage?.isClosedLost ? 'error' : 'primary'}
                      />
                    </Box>
                    
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1
                    }}>
                      ${opportunity.value?.toLocaleString() || '0'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {opportunity.probability !== null && (
                        <Chip 
                          label={`${opportunity.probability}%`} 
                          size="small"
                          color={opportunity.probability >= 75 ? 'success' : opportunity.probability >= 50 ? 'warning' : 'default'}
                        />
                      )}
                      {opportunity.stage?.name && (
                        <Chip 
                          label={opportunity.stage.name} 
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    {opportunity.expectedCloseDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Expected: {new Date(opportunity.expectedCloseDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Create Opportunity Dialog */}
        <CreateOpportunityDialog
          open={createOpportunityOpen}
          onClose={() => setCreateOpportunityOpen(false)}
          onSuccess={() => {
            fetchOpportunities();
          }}
          defaultCustomerId={record.customerId}
          defaultLeadId={record.id}
          defaultCustomerName={record.displayName || `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.company || 'Unknown Lead'}
        />
      </CardContent>
    </Card>
  );
};

const AdditionalInformationDialog = ({ open, onClose, record }: { open: boolean; onClose: () => void; record: any }) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [formData, setFormData] = useState({
    notes: record?.notes || '',
  });

  // Update form data when record changes
  React.useEffect(() => {
    if (record) {
      setFormData({
        notes: record?.notes || '',
      });
    }
  }, [record]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    if (!record) return;
    
    try {
      await update('leads', {
        id: record.id,
        data: formData,
        previousData: record,
      });
      notify('Additional information updated successfully', { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      notify('Error updating additional information', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6">Edit Additional Information</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <MuiTextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={8}
              placeholder="Add any notes about this lead including their requirements, pain points, and other relevant information..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LeadTitle = () => {
  const record = useRecordContext();
  if (!record) return 'Loading...';
  
  return (
    record.displayName ||
    `${record.firstName || ''} ${record.lastName || ''}`.trim() ||
    record.company ||
    'Lead'
  );
};

const LeadShowContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [activeTab, setActiveTab] = useState(0);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [intelligenceDialogOpen, setIntelligenceDialogOpen] = useState(false);
  const [additionalInfoDialogOpen, setAdditionalInfoDialogOpen] = useState(false);
  const [opportunitiesCount, setOpportunitiesCount] = useState(0);
  const [contactLogsCount, setContactLogsCount] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Fetch counts for tab badges
  useEffect(() => {
    if (!record?.id) return;

    const fetchCounts = async () => {
      try {
        // Get opportunities count
        const opportunitiesResponse = await dataProvider.getList('opportunities', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'createdAt', order: 'DESC' },
          filter: { leadId: record.id },
        });
        setOpportunitiesCount(opportunitiesResponse.total || 0);

        // Get contact logs count
        const contactLogsResponse = await dataProvider.getList('crm/contact-logs', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'contactDate', order: 'DESC' },
          filter: { leadId: record.id },
        });
        setContactLogsCount(contactLogsResponse.total || 0);
      } catch (error) {
        console.error('Error fetching tab counts:', error);
      }
    };

    fetchCounts();
  }, [record?.id, dataProvider]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Overview
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ContactInformationCard 
                contactDialogOpen={contactDialogOpen}
                setContactDialogOpen={setContactDialogOpen}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LeadDetailsCard 
                intelligenceDialogOpen={intelligenceDialogOpen}
                setIntelligenceDialogOpen={setIntelligenceDialogOpen}
              />
            </Grid>

            <Grid item xs={12}>
              <AdditionalInformationCard 
                additionalInfoDialogOpen={additionalInfoDialogOpen}
                setAdditionalInfoDialogOpen={setAdditionalInfoDialogOpen}
              />
            </Grid>
          </Grid>
        );
      case 1: // Opportunities
        return <OpportunitiesSection />;
      case 2: // Contact Log
        return <ContactLogTab />;
      default:
        return null;
    }
  };

  return (
    <>
      <LeadHeader />
      
      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="lead tabs">
          <Tab label="Overview" />
          <Tab 
            label={
              <Badge 
                badgeContent={opportunitiesCount} 
                color="primary"
                invisible={opportunitiesCount === 0}
                sx={{ 
                  '& .MuiBadge-badge': { 
                    right: -3,
                    top: 0,
                    fontSize: '0.75rem',
                    minWidth: '20px',
                    height: '20px',
                  } 
                }}
              >
                <Box sx={{ pr: 2 }}>Opportunities</Box>
              </Badge>
            }
          />
          <Tab 
            label={
              <Badge 
                badgeContent={contactLogsCount} 
                color="primary"
                invisible={contactLogsCount === 0}
                sx={{ 
                  '& .MuiBadge-badge': { 
                    right: -3,
                    top: 0,
                    fontSize: '0.75rem',
                    minWidth: '20px',
                    height: '20px',
                  } 
                }}
              >
                <Box sx={{ pr: 2 }}>Contact Log</Box>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {renderTabContent()}
      </Box>

      {record && (
        <>
          <LeadContactInfoDialog
            open={contactDialogOpen}
            onClose={() => setContactDialogOpen(false)}
            record={record}
          />
          <LeadIntelligenceDialog
            open={intelligenceDialogOpen}
            onClose={() => setIntelligenceDialogOpen(false)}
            record={record}
          />
          <AdditionalInformationDialog
            open={additionalInfoDialogOpen}
            onClose={() => setAdditionalInfoDialogOpen(false)}
            record={record}
          />
        </>
      )}
    </>
  );
};

export const LeadShow = () => (
  <Show actions={false} title={<LeadTitle />}>
    <Box>
      <ResourceBreadcrumbsWithActions hideEdit={true} />
      <LeadShowContent />
    </Box>
  </Show>
);
